import React, { createContext, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { isPrerendering } from '@/lib/env';
import { safeSessionStorage } from '@/lib/storage';

interface PageLoadingContextType {
  /** True only while the one-time intro loader is showing. */
  isLoading: boolean;
  /** Name shown under the loader ("Welcome", "Products", ...). */
  loadingPage: string;
  /**
   * Kept so existing callers keep compiling. Navigating no longer shows a loader: the
   * intro plays once per visit, and later pages render as soon as they are ready.
   */
  setLoading: (loading: boolean) => void;
}

const PageLoadingContext = createContext<PageLoadingContextType | undefined>(undefined);

const INTRO_SHOWN_KEY = 'introShown';
/** Long enough to read as a deliberate brand moment... */
const MIN_INTRO_MS = 900;
/** ...and never long enough to hold the page hostage. */
const MAX_INTRO_MS = 1800;

const PAGE_NAMES: Record<string, string> = {
  '/': 'Welcome',
  '/about': 'About Us',
  '/products': 'Products',
  '/services': 'Services',
  '/projects': 'Our Projects',
  '/gallery': 'Gallery',
  '/contact': 'Contact Us',
};

function getPageName(path: string): string {
  if (PAGE_NAMES[path]) return PAGE_NAMES[path];
  if (path.startsWith('/products/')) return 'Product Details';
  if (path.startsWith('/services/')) return 'Service Details';
  if (path.startsWith('/projects/')) return 'Project Details';
  return 'Page';
}

/** The intro plays once per browser session, never while a page is being prerendered. */
const shouldShowIntro = () => !isPrerendering() && safeSessionStorage.getItem(INTRO_SHOWN_KEY) === null;

interface PageLoadingProviderProps {
  children: ReactNode;
}

export const PageLoadingProvider: React.FC<PageLoadingProviderProps> = ({ children }) => {
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(shouldShowIntro);
  const [loadingPage] = useState(() => getPageName(pathname));
  const introPending = useRef(isLoading);

  // The page underneath keeps loading while the intro shows (nothing is hidden with
  // display:none), so the intro adds no waiting time beyond its own short minimum.
  useEffect(() => {
    if (!introPending.current) return;

    safeSessionStorage.setItem(INTRO_SHOWN_KEY, '1');
    const startedAt = performance.now();
    let minTimer: number | undefined;

    const finish = () => setIsLoading(false);
    const onReady = () => {
      minTimer = window.setTimeout(finish, Math.max(0, MIN_INTRO_MS - (performance.now() - startedAt)));
    };

    const maxTimer = window.setTimeout(finish, MAX_INTRO_MS);
    if (document.readyState === 'complete') onReady();
    else window.addEventListener('load', onReady, { once: true });

    return () => {
      window.clearTimeout(maxTimer);
      window.clearTimeout(minTimer);
      window.removeEventListener('load', onReady);
    };
  }, []);

  const setLoading = useCallback(() => {}, []);
  const value = useMemo(() => ({ isLoading, loadingPage, setLoading }), [isLoading, loadingPage, setLoading]);

  return <PageLoadingContext.Provider value={value}>{children}</PageLoadingContext.Provider>;
};

// Export the context for use in the hook file
export { PageLoadingContext };
